import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Eye,
  FolderKanban,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Wallet,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import { useBudgetList, useDeleteBudget } from "../hooks/useBudgets";
import { NewBudgetDrawer } from "../components/NewBudgetDrawer";
import { BudgetUtilizationBar } from "../components/BudgetUtilizationBar";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";
import type { BudgetListItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function BudgetTypeBadge({ type }: { type: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    annual: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Annual" },
    quarterly: { bg: "hsl(175,55%,92%)", fg: "hsl(175,60%,30%)", label: "Quarterly" },
    project: { bg: "hsl(270,55%,92%)", fg: "hsl(270,60%,35%)", label: "Project" },
    department: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "Department" },
    emergency: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)", label: "Emergency" },
  };
  const s = styles[type] ?? {
    bg: "hsl(210,16%,93%)",
    fg: "hsl(215,16%,40%)",
    label: type,
  };
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function BudgetStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    draft: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Draft" },
    active: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Active" },
    closed: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "Closed" },
  };
  const s = styles[status] ?? styles.draft;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

const BUDGET_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "name", label: "Name" },
  { key: "type", label: "Type", defaultVisible: false },
  { key: "entity", label: "Entity" },
  { key: "totalAmount", label: "Total" },
  { key: "usedAmount", label: "Used" },
  { key: "remainingAmount", label: "Remaining" },
  { key: "utilization", label: "Utilization" },
  { key: "status", label: "Status" },
];

const iconBtnBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-muted hover:text-foreground";
const iconBtnDelete =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]";

export default function BudgetListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("budgets", BUDGET_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      type: typeFilter || undefined,
    }),
    [search, statusFilter, typeFilter],
  );

  const { data, isLoading, isError, error } = useBudgetList({
    page,
    limit: pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
    type: typeFilter || undefined,
  });

  const deleteBudget = useDeleteBudget();

  const budgets = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  function handleDelete(b: BudgetListItem) {
    setDeleteTarget({ id: b.id, name: b.name });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteBudget.mutateAsync(deleteTarget.id);
    showToast("Budget deleted");
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Budgets" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Budgets</h1>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              module="budgets"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={BUDGET_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Budget
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load budgets</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px] shrink-0">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search budgets..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="w-40 shrink-0"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </Select>
              <Select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
                className="w-40 shrink-0"
              >
                <option value="">All Types</option>
                <option value="annual">Annual</option>
                <option value="quarterly">Quarterly</option>
                <option value="project">Project</option>
                <option value="department">Department</option>
                <option value="emergency">Emergency</option>
              </Select>
            </div>

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading budgets…
                </div>
              ) : budgets.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No budgets match the current filters.
                </div>
              ) : (
                <table
                  className="w-full text-[13px]"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {isVisible("code") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          #
                        </th>
                      )}
                      {isVisible("name") && (
                        <th className="px-[14px] py-[9px]">Name</th>
                      )}
                      {isVisible("type") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          Type
                        </th>
                      )}
                      {isVisible("entity") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 180 }}>
                          Entity
                        </th>
                      )}
                      {isVisible("totalAmount") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
                          Total
                        </th>
                      )}
                      {isVisible("usedAmount") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
                          Used
                        </th>
                      )}
                      {isVisible("remainingAmount") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
                          Remaining
                        </th>
                      )}
                      {isVisible("utilization") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 160 }}>
                          Utilization
                        </th>
                      )}
                      {isVisible("status") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 100 }}>
                          Status
                        </th>
                      )}
                      <th
                        className="px-[14px] py-[9px] text-right"
                        style={{ width: 120 }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {budgets.map((b) => {
                      const remainingPct =
                        b.totalAmount > 0
                          ? (b.remainingAmount / b.totalAmount) * 100
                          : 0;
                      const remainingLow = remainingPct < 10;
                      const contextName =
                        b.projectName ?? b.productName ?? b.companyName ?? null;
                      const contextIcon = b.projectName
                        ? FolderKanban
                        : Building2;
                      const ContextIcon = contextIcon;
                      return (
                        <tr
                          key={b.id}
                          className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                          onClick={() => navigate(`/budgets/${b.id}`)}
                        >
                          {isVisible("code") && (
                            <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                              {b.code}
                            </td>
                          )}
                          {isVisible("name") && (
                            <td className="px-[14px] py-[11px]">
                              <Link
                                to={`/budgets/${b.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-medium text-foreground hover:text-[hsl(210,100%,35%)]"
                              >
                                {truncate(b.name, 40)}
                              </Link>
                              {b.fiscalYear && (
                                <div className="text-[11px] text-muted-foreground">
                                  FY {b.fiscalYear}
                                </div>
                              )}
                            </td>
                          )}
                          {isVisible("type") && (
                            <td className="px-[14px] py-[11px]">
                              <BudgetTypeBadge type={b.type} />
                            </td>
                          )}
                          {isVisible("entity") && (
                            <td className="px-[14px] py-[11px] text-muted-foreground">
                              {contextName ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <ContextIcon
                                    className="h-3 w-3"
                                    strokeWidth={1.5}
                                  />
                                  <span className="truncate">{contextName}</span>
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          {isVisible("totalAmount") && (
                            <td className="px-[14px] py-[11px] text-foreground">
                              {formatCurrency(b.totalAmount, b.currency)}
                            </td>
                          )}
                          {isVisible("usedAmount") && (
                            <td className="px-[14px] py-[11px] text-foreground">
                              {formatCurrency(b.usedAmount, b.currency)}
                            </td>
                          )}
                          {isVisible("remainingAmount") && (
                            <td
                              className={`px-[14px] py-[11px] ${
                                remainingLow
                                  ? "font-semibold text-[hsl(0,70%,45%)]"
                                  : "text-foreground"
                              }`}
                            >
                              {formatCurrency(b.remainingAmount, b.currency)}
                            </td>
                          )}
                          {isVisible("utilization") && (
                            <td className="px-[14px] py-[11px]">
                              <div className="flex items-center gap-2">
                                <div className="w-[80px]">
                                  <BudgetUtilizationBar
                                    totalAmount={b.totalAmount}
                                    usedAmount={b.usedAmount}
                                    currency={b.currency}
                                    size="sm"
                                    showLabels={false}
                                  />
                                </div>
                                <span className="text-[12px] text-muted-foreground">
                                  {Math.round(b.utilizationPercent)}%
                                </span>
                              </div>
                            </td>
                          )}
                          {isVisible("status") && (
                            <td className="px-[14px] py-[11px]">
                              <BudgetStatusBadge status={b.status} />
                            </td>
                          )}
                          <td
                            className="relative px-[14px] py-[11px] text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/budgets/${b.id}`);
                                }}
                              >
                                <Eye className="h-[15px] w-[15px]" strokeWidth={1.5} />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/budgets/${b.id}`);
                                }}
                              >
                                <Pencil className="h-[15px] w-[15px]" strokeWidth={1.5} />
                              </button>
                              <button
                                type="button"
                                className={iconBtnDelete}
                                title="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(b);
                                }}
                              >
                                <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.5} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-3">
                  <span>
                    Showing {start}-{end} of {total} entries
                  </span>
                  <span className="flex items-center gap-1.5">
                    Show
                    <Select
                      value={String(pageSize)}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-7 w-[68px] text-xs"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Select>
                    entries
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="px-2 text-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewBudgetDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(code) => showToast(`Budget created: ${code}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete budget?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Budgets list.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
